import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Button as MUIButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getConfigs } from "app/reducers/configSlice";
import { getDeviceActionState } from "app/reducers/devicesSlice";
import { getSlotState } from "app/reducers/slotsSlice";
import { fetchConfigs } from "app/thunks/configAPI";
import { fetchDevices } from "app/thunks/devicesAPI";
import { fetchSlots, linkSlots } from "app/thunks/slotsAPI";
import classNames from "classnames";
import { NAVIGATOR } from "features/navigation";
import { SESSION_STATUS } from "interfaces/Sessions";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import {
  BUTTON_STYLES,
  BUTTON_TYPES,
  Spinner,
  Title,
  useCustomToast,
} from "ui";
import { Select } from "ui/Form/Select";

export interface LinkData {
  slotId: string;
  deviceId: string | null;
}

export const LinkScreen = () => {
  const history = useHistory();
  const [selectedValues, setSelectedValues] = useState<LinkData[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const {
    headsets,
    isFetching: isLoadingHeadsets,
    isError,
  } = useAppSelector(getDeviceActionState);
  const {
    slots,
    isFetching: isLoadingSlots,
    error,
    isLinking,
  } = useAppSelector(getSlotState);
  const { showSuccessToast, showErrorToast } = useCustomToast();

  useEffect(() => {
    dispatch(fetchConfigs());
  }, [dispatch]);

  const { isFetching, configs } = useAppSelector(getConfigs);

  const isSessionStarted = useMemo(() => {
    return configs.some((config) =>
      config.sessions.some(
        (session) =>
          session.status === SESSION_STATUS.IN_PROGRESS ||
          session.status === SESSION_STATUS.READY ||
          session.status === SESSION_STATUS.STANDBY,
      ),
    );
  }, [configs]);

  const initialValues = useMemo(
    () =>
      slots.map(({ _id, deviceId }) => ({
        slotId: _id,
        deviceId,
      })),
    [slots],
  );

  useEffect(() => {
    const changesDetected =
      JSON.stringify(initialValues) !== JSON.stringify(selectedValues);
    setHasChanges(changesDetected);
  }, [selectedValues, initialValues]);

  useEffect(() => {
    if (!isSessionStarted) {
      dispatch(fetchSlots());
      const intervalId = setInterval(() => {
        dispatch(fetchDevices());
        setInitialLoad(true);
      }, 1000);
      return () => clearInterval(intervalId);
    }
  }, [dispatch, isSessionStarted]);

  useEffect(() => {
    if (slots)
      setSelectedValues(
        slots.map(({ _id, deviceId }) => ({
          slotId: _id,
          deviceId,
        })),
      );
  }, [dispatch, slots]);

  const headsetsOptions = useMemo(() => {
    if (!headsets) return [];
    return headsets.map(({ description, _id }) => ({
      label: description,
      value: _id,
    }));
  }, [headsets]);

  const filteredOptions = useMemo(() => {
    const selectedHeadsetIds = selectedValues.map((item) => item.deviceId);
    return headsetsOptions.filter(
      (option) => !selectedHeadsetIds.includes(option.value),
    );
  }, [headsetsOptions, selectedValues]);

  const handleSelectChange = (
    slotId: string,
    selectedOption: React.ChangeEvent<any>,
  ) => {
    setSelectedValues((prev) => {
      const updatedValues = prev.map((item) =>
        item.slotId === slotId
          ? { ...item, deviceId: selectedOption?.target.value || null }
          : item,
      );
      return updatedValues;
    });
  };

  const handleLink = async () => {
    try {
      await dispatch(linkSlots({ slots: selectedValues })).unwrap();

      showSuccessToast({
        title: "¡Listo!",
        message: "Los cascos ya están vinculados!",
      });
      await dispatch(fetchSlots()).unwrap();
    } catch (_) {
      showErrorToast({
        title: "Lo sentimos",
        message: "No pudimos vincular los cascos",
      });
    }
  };

  const handleNavigation = () => {
    history.push(NAVIGATOR.sessions);
  };

  if (
    (isLoadingHeadsets && !initialLoad) ||
    isLoadingSlots ||
    isError ||
    error ||
    !slots ||
    !selectedValues.length ||
    !configs ||
    isFetching
  ) {
    return (
      <div className="flex w-full justify-center items-center bg-faux-dark-gunmetal h-screen">
        <Spinner size={60} />
      </div>
    );
  }

  if (isSessionStarted) {
    return (
      <div className="flex-col w-full min-h-screen px-14 pt-16 bg-faux-dark-gunmetal">
        <Title text="Vinculaciones" className="mb-[200px]" />
        <div className="flex flex-col w-full justify-center items-center bg-faux-dark-gunmetal gap-4">
          <WarningAmberIcon
            fontSize="large"
            className="text-yellow-400 w-[2em] h-[2em]"
            aria-hidden="true"
          />
          <p className="text-white text-2xl">
            No se pueden realizar cambios en Vinculaciones porque existen
            sesiones activas.
          </p>
          <p className="text-white text-2xl">Finalícelas para continuar.</p>
          <MUIButton
            onClick={handleNavigation}
            variant="contained"
            className={classNames("w-48", BUTTON_STYLES[BUTTON_TYPES.HOME])}
          >
            Ver sesiones
          </MUIButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-col w-full min-h-screen px-14 pt-16 pb-8 bg-faux-dark-gunmetal">
      <Title text="Vinculaciones" />
      <div className=" w-2/3 m-auto mb-8">
        <p>
          Para realizar la vinculación deberás iniciar sesión dentro de cada
          dispositivo.
        </p>
      </div>
      <div className="flex flex-col w-2/3 h-2/3 m-auto justify-between border-white border-2 rounded-md border-opacity-40">
        <div className="flex flex-col gap-2 h-full pt-8 overflow-y-scroll">
          {slots.map(({ _id, number }) => {
            const value =
              headsetsOptions.find(
                (option) =>
                  option.value ===
                  selectedValues.find((item) => item.slotId === _id)?.deviceId,
              ) || null;
            return (
              <div className="flex w-full items-center px-8" key={_id}>
                <p className="mb-4 text-lg font-semibold mx-14 w-20 text-left">{`Casco ${number}`}</p>
                <div className="w-1/2">
                  <Select
                    name={`headset-${_id}`}
                    options={[
                      ...(value?.value
                        ? [{ label: "Quitar dispositivo", value: null }]
                        : filteredOptions.length
                          ? []
                          : [
                              {
                                label: "Esperando conexión con un dispositivo",
                                value: null,
                              },
                            ]),
                      ...filteredOptions,
                    ]}
                    placeholder="Seleccionar dispositivo"
                    value={value}
                    onChange={(selectedOption) =>
                      handleSelectChange(_id, selectedOption)
                    }
                    disableOptionMap
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end mt-4 pb-8 pr-8">
          <MUIButton
            variant="contained"
            disabled={!hasChanges}
            onClick={handleLink}
            className={classNames("w-48", BUTTON_STYLES[BUTTON_TYPES.AUTH])}
          >
            {isLinking ? <Spinner color="white" /> : <p>Vincular</p>}
          </MUIButton>
        </div>
      </div>
      <div className="flex justify-center mt-12">
        <MUIButton
          variant="contained"
          disabled={!initialValues.some(({ deviceId }) => !!deviceId)}
          onClick={() => history.push(NAVIGATOR.configs)}
          className={classNames("w-1/3", BUTTON_STYLES[BUTTON_TYPES.AUTH])}
        >
          Continuar seteo
        </MUIButton>
      </div>
    </div>
  );
};
